import { create } from 'zustand'
import { Mode } from '@/types/profile'

type ProfileMode = Mode | 'NONE'

interface ProfileModeState {
  mode: ProfileMode
  loading: boolean
  hasFreelancer: boolean
  hasCompany: boolean
  lastFetchTime: number | null
  isFetching: boolean
  
  // Actions
  fetchActive: () => Promise<void>
  switchMode: (next: ProfileMode) => Promise<void>
  reset: () => void
}

// 캐시 시간: 5초 (같은 요청이 5초 이내에 발생하면 캐시 사용)
const CACHE_DURATION = 5000

export const useProfileModeStore = create<ProfileModeState>((set, get) => ({
  mode: 'NONE',
  loading: true,
  hasFreelancer: false,
  hasCompany: false,
  lastFetchTime: null,
  isFetching: false,

  fetchActive: async () => {
    // 이미 fetch 중이면 중복 호출 방지
    if (get().isFetching) {
      return
    }

    // 캐시가 유효하면 API 호출하지 않음
    const now = Date.now()
    const lastFetch = get().lastFetchTime
    if (lastFetch && now - lastFetch < CACHE_DURATION) {
      // 캐시가 유효하지만 loading이 true면 false로 변경
      if (get().loading) {
        set({ loading: false })
      }
      return
    }

    // 클라이언트에서만 실행
    if (typeof window === 'undefined') {
      set({ loading: false })
      return
    }

    set({ isFetching: true, loading: true })

    try {
      const res = await fetch('/api/profile/active', { cache: 'no-store' })
      
      if (!res.ok) {
        // 401 Unauthorized는 비로그인 상태이므로 조용히 처리 (에러 로그 출력하지 않음)
        if (res.status === 401) {
          set({
            hasFreelancer: false,
            hasCompany: false,
            mode: 'NONE',
            loading: false,
            isFetching: false,
          })
          return
        }
        
        // 그 외 에러는 로그 출력
        const errorData = await res.json().catch(() => ({ error: 'Unknown error' }))
        console.error('[useProfileModeStore] API 에러:', res.status, errorData)
        
        set({
          hasFreelancer: false,
          hasCompany: false,
          mode: 'NONE',
          loading: false,
          isFetching: false,
        })
        return
      }
      
      const json = await res.json()

      set({
        hasFreelancer: json.hasFreelancer || false,
        hasCompany: json.hasCompany || false,
        mode: json.activeProfile?.profile_type === 'FREELANCER'
          ? 'FREELANCER'
          : json.activeProfile?.profile_type === 'COMPANY'
          ? 'COMPANY'
          : json.hasFreelancer
          ? 'FREELANCER'
          : json.hasCompany
          ? 'COMPANY'
          : 'NONE',
        loading: false,
        isFetching: false,
        lastFetchTime: Date.now(),
      })
    } catch (error) {
      console.error('[useProfileModeStore] fetchActive 에러:', error)
      
      set({
        hasFreelancer: false,
        hasCompany: false,
        mode: 'NONE',
        loading: false,
        isFetching: false,
      })
    }
  },

  switchMode: async (next: ProfileMode) => {
    const currentMode = get().mode
    const { hasFreelancer, hasCompany } = get()
    
    if (next === 'NONE' || next === currentMode) return
    if (next === 'COMPANY' && !hasCompany) return
    if (next === 'FREELANCER' && !hasFreelancer) return

    try {
      const res = await fetch('/api/profile/switch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ profileType: next }),
      })

      if (!res.ok) {
        const error = await res.json()
        throw new Error(error.error || '프로필 전환 실패')
      }

      const json = await res.json()
      set({ 
        mode: json.activeProfile.profile_type as Mode,
        lastFetchTime: Date.now(), // 캐시 갱신
      })

      // profileSwitched 이벤트 발생
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event('profileSwitched'))
      }
    } catch (error) {
      console.error('[useProfileModeStore] switchMode 에러:', error)
      throw error
    }
  },

  reset: () => {
    set({
      mode: 'NONE',
      loading: true,
      hasFreelancer: false,
      hasCompany: false,
      lastFetchTime: null,
      isFetching: false,
    })
  },
}))

// Hook 형태로 사용할 수 있도록 래퍼 제공
export const useProfileMode = () => {
  const store = useProfileModeStore()
  
  // 컴포넌트 마운트 시 한 번만 fetch (useEffect는 hook 사용하는 컴포넌트에서 처리)
  return {
    mode: store.mode === 'NONE' ? null : store.mode,
    isFreelancer: store.mode === 'FREELANCER',
    isCompany: store.mode === 'COMPANY',
    hasFreelancer: store.hasFreelancer,
    hasCompany: store.hasCompany,
    switchMode: store.switchMode,
    loading: store.loading,
    fetchActive: store.fetchActive, // 수동 호출 가능
  }
}

